// Catálogos y cálculo puro de 028A/028B/028C (controles del niño), fieles al
// PDF oficial AM-00115-2021 (028A págs. 116-117, 028B 124-125, 028C 149-150).
// Viven aparte de los .tsx para poder probarlos sin renderizar.
import { imcDe } from './calculosClinicosDatos';
import { leerSiNo, textoSiNo, type SiNoValor } from './camposSimplesDatos';
import type { Par } from './index';

/** 028A, D. Antecedentes natales (9 ítems del PDF, en su orden). */
export const ANTECEDENTES_NATALES_028A = [
  'roturaPrematuraMembranas',
  'partoInstitucional',
  'partoEspontaneo',
  'presentacionCefalica',
  'partoMultiple',
  'cesarea',
  'complicacionesIntraparto',
  'anestesia',
  'medicamentos',
] as const;

/** 028A, E. Antecedentes postnatales con Sí/No + observación (el PDF no trae tamizaje visual). */
export const ANTECEDENTES_POSTNATALES_SINO_028A = [
  'reanimacion',
  'oxigenoterapia',
  'otrasEnfermedades',
  'hospitalizacion',
  'apegoInmediato',
  'lactanciaInmediata',
  'tamizajeMetabolico',
  'tamizajeAuditivo',
  'consejeria',
  'alergias',
  'vacunas',
  'defectosCongenitos',
] as const;

/** 028A, F. AIEPI del menor de 2 meses (9 ítems). */
export const AIEPI_MENOR_2_MESES = ['signosGraves', 'ictericia', 'diarrea', 'problemaAlimentacion', 'recibioBcg', 'recibioHb', 'otroProblema', 'maltrato', 'problemaDesarrollo'] as const;

/** 028B, H. Examen físico: 17 ítems en el orden del PDF (13 = Genito-urinario, 14 = Ano-rectal). */
export const EXAMEN_FISICO_028B = [
  'pielAnexos',
  'cabeza',
  'ojos',
  'oidos',
  'nariz',
  'boca',
  'oroFaringe',
  'cuello',
  'axilaMamas',
  'torax',
  'abdomen',
  'columnaVertebral',
  'generoUrinario',
  'anoRectal',
  'miembrosSuperiores',
  'miembrosInferiores',
  'neurologico',
] as const;

/** 028C, G. Revisión actual de órganos y sistemas (10 ítems; el 6 es Genito-urinario). */
export const REVISION_SISTEMAS_028C = ['pielAnexos', 'organosSentidos', 'respiratorio', 'cardioVascular', 'digestivo', 'generoUrinario', 'musculoEsqueletico', 'endocrino', 'hemoLinfatico', 'nervioso'] as const;

/** 028C, H. Examen físico regional (1R-15R; 1R Piel-Faneras, 13R Ingle-periné). */
export const EXAMEN_FISICO_REGIONAL_028C = [
  'pielFaneras',
  'cabeza',
  'ojos',
  'oidos',
  'nariz',
  'boca',
  'oroFaringe',
  'cuello',
  'axilaMamas',
  'torax',
  'abdomen',
  'columnaVertebral',
  'inglePerine',
  'miembrosSuperiores',
  'miembrosInferiores',
] as const;

/** 028C, H. Examen físico sistémico (1S-10S; 8S Endócrino). */
export const EXAMEN_FISICO_SISTEMICO_028C = ['organosSentidos', 'respiratorio', 'cardioVascular', 'digestivo', 'genital', 'urinario', 'musculoEsqueletico', 'endocrino', 'hemoLinfatico', 'neurologico'] as const;

// `imcDe` ya ignora las tallas a medio teclear ("1", "13") y fuera de rango (calculosClinicosDatos.ts).
const imcDeConstantes = imcDe;

/**
 * 028C: al cambiar peso o talla, recalcula el IMC. Si el cálculo deja de ser
 * posible, borra el IMC solo cuando era el autocalculado; uno escrito a mano se conserva.
 */
export function constantesConImc<T extends { peso: string; talla: string; imc: string }>(constantes: T, campo: keyof T, valor: string): T {
  const siguiente = { ...constantes, [campo]: valor };
  if (campo !== 'peso' && campo !== 'talla') return siguiente;
  const antes = imcDeConstantes(constantes.peso, constantes.talla);
  const ahora = imcDeConstantes(siguiente.peso, siguiente.talla);
  if (ahora !== undefined) return { ...siguiente, imc: String(ahora) };
  return antes !== undefined && constantes.imc === String(antes) ? { ...siguiente, imc: '' } : siguiente;
}

/** Ítem SI/NO + observación del papel (028A D y E): tri-estado, ver `camposSimplesDatos.SiNoValor`. */
export interface ItemSiNoObs {
  readonly valor: SiNoValor;
  readonly observacion: string;
}
export const ITEM_SI_NO_OBS_VACIO: ItemSiNoObs = { valor: '', observacion: '' };

export interface TextosSiNo {
  readonly si: string;
  readonly no: string;
}

/**
 * Un `Par` por ítem contestado: 'Sí' / 'No' (+ ' — observación'). Sin responder y sin observación no sale.
 * Lee también el estado viejo `{ marcado: boolean }` (true -> Sí; false -> sin responder).
 */
export function paresSiNoObs(
  claves: readonly string[],
  valores: Readonly<Record<string, ItemSiNoObs | { readonly marcado?: boolean; readonly observacion?: string } | undefined>>,
  preguntaDe: (clave: string) => string,
  textos: TextosSiNo,
): Par[] {
  const pares: Par[] = [];
  for (const clave of claves) {
    const item = valores[clave] as { valor?: unknown; marcado?: unknown; observacion?: string } | undefined;
    const respuesta = textoSiNo(leerSiNo(item?.valor ?? item?.marcado), textos);
    const obs = (item?.observacion ?? '').trim();
    const texto = [respuesta, obs].filter(Boolean).join(' — ');
    if (texto) pares.push({ pregunta: preguntaDe(clave), respuesta: texto });
  }
  return pares;
}

/** Un `Par` por ítem SI/NO sin observación (028A F, AIEPI): 'Sí' / 'No'; nada si sin responder. */
export function paresSiNoSimples(claves: readonly string[], valores: Readonly<Record<string, unknown>>, preguntaDe: (clave: string) => string, textos: TextosSiNo): Par[] {
  return claves.flatMap((clave) => {
    const respuesta = textoSiNo(valores[clave], textos);
    return respuesta ? [{ pregunta: preguntaDe(clave), respuesta }] : [];
  });
}
